<template>
  <div class="api-config-step">
    <!-- OpenAPI / Swagger Import -->
    <shared-open-api-import-panel @apply-config="applyOpenApiConfig" />

    <!-- Data Source Name -->
    <v-text-field
      v-model="config.name"
      label="Data Source Name"
      placeholder="e.g. Equipment API, SAP Export"
      outlined
      dense
      :rules="[rules.required]"
      @input="emitConfig"
    />

    <!-- URL + Method Row -->
    <v-row dense>
      <v-col cols="3" sm="2">
        <v-select
          v-model="config.httpMethod"
          :items="HttpMethodOptions"
          label="Method"
          outlined
          dense
          @change="emitConfig"
        >
          <template v-slot:selection="{ item }">
            <v-chip
              x-small
              :color="httpMethodColors[item.value]"
              dark
              label
              class="font-weight-bold"
            >
              {{ item.text }}
            </v-chip>
          </template>
          <template v-slot:item="{ item }">
            <v-chip
              x-small
              :color="httpMethodColors[item.value]"
              dark
              label
              class="font-weight-bold"
            >
              {{ item.text }}
            </v-chip>
          </template>
        </v-select>
      </v-col>
      <v-col>
        <v-text-field
          v-model="config.url"
          label="API URL"
          placeholder="https://api.example.com/v1/data"
          outlined
          dense
          :rules="[rules.required, rules.validUrl]"
          @input="emitConfig"
        />
      </v-col>
    </v-row>

    <!-- Request Body (POST only) -->
    <v-expand-transition>
      <v-textarea
        v-if="config.httpMethod === 'POST'"
        v-model="config.requestBody"
        label="Request Body (JSON)"
        placeholder='{ "filters": { "status": "active" } }'
        outlined
        dense
        rows="3"
        auto-grow
        class="code-font"
        @input="emitConfig"
      />
    </v-expand-transition>

    <!-- Response Data Path -->
    <v-text-field
      v-model="config.responseDataPath"
      label="Response Data Path"
      placeholder="e.g. data.items, results, response.records"
      hint="Path to the data array in the JSON response. Leave empty if root is the array."
      persistent-hint
      outlined
      dense
      @input="emitConfig"
    />

    <!-- Headers Section (Expandable) -->
    <v-expansion-panels flat class="mt-4 headers-panel">
      <v-expansion-panel>
        <v-expansion-panel-header class="pa-0">
          <div class="d-flex align-center">
            <span class="text-subtitle-2 font-weight-medium">Headers</span>
            <v-chip
              v-if="config.headers.length"
              x-small
              color="primary"
              dark
              class="ml-2"
            >
              {{ config.headers.length }}
            </v-chip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="pa-0">
          <v-row v-for="(header, index) in config.headers" :key="index" dense class="mb-1">
            <v-col cols="5">
              <v-text-field
                v-model="header.key"
                placeholder="Header Name"
                outlined
                dense
                hide-details
                @input="emitConfig"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="header.value"
                placeholder="Header Value"
                outlined
                dense
                hide-details
                @input="emitConfig"
              />
            </v-col>
            <v-col cols="auto">
              <v-btn icon small @click="removeHeader(index)">
                <v-icon small color="error">mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn x-small text color="primary" class="mt-1" @click="addHeader">
            <v-icon x-small left>mdi-plus</v-icon> Add Header
          </v-btn>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Authentication Section -->
    <div class="section-header mt-4 mb-2">
      <span class="text-subtitle-2 font-weight-medium">Authentication</span>
    </div>

    <v-select
      v-model="config.auth.authType"
      :items="AuthTypeOptions"
      label="Auth Type"
      outlined
      dense
      @change="emitConfig"
    />

    <!-- API Key fields -->
    <v-expand-transition>
      <v-row v-if="config.auth.authType === AuthType.ApiKey" dense>
        <v-col cols="5">
          <v-text-field
            v-model="config.auth.apiKeyHeaderName"
            label="Header Name"
            placeholder="X-API-Key"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="config.auth.apiKeyValue"
            label="API Key"
            type="password"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Bearer Token -->
    <v-expand-transition>
      <v-text-field
        v-if="config.auth.authType === AuthType.BearerToken"
        v-model="config.auth.bearerToken"
        label="Bearer Token"
        type="password"
        outlined
        dense
        @input="emitConfig"
      />
    </v-expand-transition>

    <!-- Basic Auth -->
    <v-expand-transition>
      <v-row v-if="config.auth.authType === AuthType.BasicAuth" dense>
        <v-col>
          <v-text-field
            v-model="config.auth.username"
            label="Username"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="config.auth.password"
            label="Password"
            type="password"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- OAuth2 Client Credentials -->
    <v-expand-transition>
      <div v-if="config.auth.authType === AuthType.OAuth2ClientCredentials">
        <v-text-field
          v-model="config.auth.tokenUrl"
          label="Token URL"
          placeholder="https://auth.example.com/oauth/token"
          outlined
          dense
          @input="emitConfig"
        />
        <v-row dense>
          <v-col>
            <v-text-field
              v-model="config.auth.clientId"
              label="Client ID"
              outlined
              dense
              @input="emitConfig"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="config.auth.clientSecret"
              label="Client Secret"
              type="password"
              outlined
              dense
              @input="emitConfig"
            />
          </v-col>
        </v-row>
        <v-text-field
          v-model="config.auth.scope"
          label="Scope (optional)"
          placeholder="read write"
          outlined
          dense
          @input="emitConfig"
        />
      </div>
    </v-expand-transition>

    <!-- Pagination Section -->
    <div class="section-header mt-4 mb-2">
      <span class="text-subtitle-2 font-weight-medium">Pagination</span>
    </div>

    <v-select
      v-model="config.pagination.type"
      :items="PaginationTypeOptions"
      label="Pagination Type"
      outlined
      dense
      @change="emitConfig"
    />

    <!-- Page Number pagination -->
    <v-expand-transition>
      <v-row v-if="config.pagination.type === PaginationType.PageNumber" dense>
        <v-col>
          <v-text-field
            v-model="config.pagination.pageParamName"
            label="Page Param"
            placeholder="page"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="config.pagination.pageSizeParamName"
            label="Page Size Param"
            placeholder="pageSize"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model.number="config.pagination.pageSizeValue"
            label="Page Size"
            type="number"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Offset pagination -->
    <v-expand-transition>
      <v-row v-if="config.pagination.type === PaginationType.Offset" dense>
        <v-col>
          <v-text-field
            v-model="config.pagination.offsetParamName"
            label="Offset Param"
            placeholder="offset"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="config.pagination.limitParamName"
            label="Limit Param"
            placeholder="limit"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model.number="config.pagination.limitValue"
            label="Limit"
            type="number"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
      </v-row>
    </v-expand-transition>

    <!-- Cursor pagination -->
    <v-expand-transition>
      <v-row v-if="config.pagination.type === PaginationType.CursorBased" dense>
        <v-col>
          <v-text-field
            v-model="config.pagination.cursorParamName"
            label="Cursor Param"
            placeholder="cursor"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="config.pagination.cursorResponsePath"
            label="Cursor Path in Response"
            placeholder="meta.nextCursor"
            outlined
            dense
            @input="emitConfig"
          />
        </v-col>
      </v-row>
    </v-expand-transition>
  </div>
</template>

<script>
import {
  AuthType,
  AuthTypeOptions,
  HttpMethodOptions,
  PaginationType,
  PaginationTypeOptions,
} from '@/features/Share/config/apiDataSourceEnums';
import SharedOpenApiImportPanel from './SharedOpenApiImportPanel.vue';

export default {
  name: 'SharedApiConfigStep',
  components: {
    SharedOpenApiImportPanel,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      AuthType,
      AuthTypeOptions,
      HttpMethodOptions,
      PaginationType,
      PaginationTypeOptions,
      httpMethodColors: {
        GET: 'green',
        POST: 'orange darken-1',
      },
      config: { ...this.value },
      rules: {
        required: v => !!v || 'Required',
        validUrl: v => {
          if (!v) return true;
          try {
            new URL(v);
            return true;
          } catch {
            return 'Enter a valid URL';
          }
        },
      },
    };
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.config = { ...newVal };
      },
    },
  },
  methods: {
    emitConfig() {
      this.$emit('input', { ...this.config });
    },
    addHeader() {
      this.config.headers.push({ key: '', value: '' });
      this.emitConfig();
    },
    removeHeader(index) {
      this.config.headers.splice(index, 1);
      this.emitConfig();
    },
    applyOpenApiConfig(patch) {
      if (patch.name) this.config.name = patch.name;
      if (patch.url) this.config.url = patch.url;
      if (patch.httpMethod) this.config.httpMethod = patch.httpMethod;
      if (patch.requestBody !== undefined) this.config.requestBody = patch.requestBody;
      if (patch.responseDataPath !== undefined) this.config.responseDataPath = patch.responseDataPath;
      if (patch.headers && patch.headers.length) {
        this.config.headers = [...patch.headers];
      }
      if (patch.auth) {
        this.config.auth = { ...this.config.auth, ...patch.auth };
      }
      this.emitConfig();
    },
  },
};
</script>

<style scoped>
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.code-font {
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
}

.headers-panel .v-expansion-panel {
  background: transparent;
}

.headers-panel .v-expansion-panel::before {
  box-shadow: none;
}

.headers-panel .v-expansion-panel-content::v-deep .v-expansion-panel-content__wrap {
  padding: 8px 0 0;
}
</style>
